<template lang="html">
  <div>
    <b-row class="mt-5">
      <b-col md="8" offset="2">
        <h1>Import your show's media</h1>
        <h2 class="h4 mt-3">
          Each show needs an audio file, and at least one image.
        </h2>
        <p v-if="$store.getters.is_trial" class="alert alert-info mt-4">
          Trial accounts are enabled for up to
          <strong>{{ $store.state.imageImportLimit }}</strong> images.
          <b-link :to="{ name: 'upgrade_view' }">Upgrade</b-link>
          for the full experience.
        </p>
      </b-col>
    </b-row>
    <!-- Images -->
    <b-row v-if="!uploadingHasBegun" class="mt-5">
      <b-col md="2" offset="2">
        <b-btn size="md" variant="primary" @click="selectImages">
          Import images <v-icon name="images" />
        </b-btn>
      </b-col>
      <b-col v-if="image_files.length > 0" md="6">
        <h2>
          <v-icon name="check" class="mr-2" />
          {{ image_files.length }} images selected
        </h2>
      </b-col>
      <b-col v-if="image_files.length == 0" md="6">
        <p class="">
          For best results, your images should be sized larger than 1600 pixels
          at their largest dimension.
        </p>
        <p class="small">
          Accepted formats:
          {{ $store.state.acceptedImageFormats.split(",").join(", ") }}
        </p>
      </b-col>
      <b-col
        v-if="selectedImagesTotal > $store.state.imageImportLimit"
        md="8"
        offset="2"
      >
        <!-- Over importing warning. -->
        <ImageLimitImportWarning
          :selected-images-total="selectedImagesTotal"
          @select="selectImages"
        />
        <!-- END // Over importing warning. -->
      </b-col>
    </b-row>

    <!-- Video -->
    <b-row
      v-if="!uploadingHasBegun && $store.state.user_doc.beta_tester"
      class="mt-5"
    >
      <b-col md="2" offset="2">
        <b-btn size="md" variant="primary" @click="selectVideo">
          Import videos <v-icon name="video" />
        </b-btn>
      </b-col>

      <b-col v-if="validVideos.length > 0 && !isOverVideoLimit" md="6">
        <h2>
          <v-icon name="check" class="mr-2" />
          {{ validVideos.length }}
          {{ "video" | pluralize(validVideos.length) }} selected for import
        </h2>
      </b-col>

      <b-col v-if="validVideos.length > 0 && isOverVideoLimit" md="6">
        <!-- Over importing warning. -->
        <p class="alert alert-warning">
          Too many videos selected. Please re-select up to
          {{ $store.state.videoImportLimit }} video files.
        </p>
        <!-- END // Over importing warning. -->
      </b-col>

      <b-col v-if="validVideos.length == 0" md="6">
        <p>
          <span class="mark mr-2">
            <v-icon name="flask" class="mr-1" /> Video slides are a beta
            feature.
          </span>
          Video clips must be less than {{ videoDurationLimitSeconds }} seconds.
          For best results, import 1080p files.
        </p>
        <p class="small">
          Accepted formats: mp4, mov. Account limit of
          {{ $store.state.videoImportLimit }} videos per show.
        </p>
      </b-col>
    </b-row>

    <!-- Invalid video warning -->
    <b-row v-if="$store.state.user_doc.beta_tester && invalidVideos.length > 0">
      <b-col offset="2" md="8" class="mt-3 alert alert-warning">
        <p v-if="invalidVideos.length > 0" class="mt-1">
          Warning: {{ invalidVideos.length }} selected
          {{ "video" | pluralize(invalidVideos.length) }} exceeded
          {{ videoDurationLimitSeconds }} seconds.
        </p>
        <p v-for="i in invalidVideos" :key="i.name" class="small">
          <strong>{{ i.name }}</strong> will not be imported.
        </p>
      </b-col>
    </b-row>

    <!-- Audio -->
    <b-row v-if="!uploadingHasBegun" class="mt-5">
      <b-col md="2" offset="2">
        <b-btn
          size="md"
          variant="primary"
          :disabled="dataAlreadyUploaded"
          @click="selectAudio"
        >
          Import audio <v-icon name="headphones" />
        </b-btn>
      </b-col>

      <b-col v-if="audio_file || dataAlreadyUploaded" md="6">
        <h2 v-if="audio_file">
          <v-icon name="check" class="mr-2" />
          Audio selected
        </h2>
        <h2 v-else>
          <v-icon name="check" class="mr-2" />
          Audio uploaded
        </h2>
      </b-col>
      <b-col v-if="!audio_file && !dataAlreadyUploaded" md="6">
        <p class="">
          Select your audio file.
        </p>
        <p class="small">
          Accepted formats:
          {{ $store.state.acceptedAudioFormats.split(",").join(", ") }}
        </p>
        <p class="mt-4">
          Or select from our curated collection of pre-licensed music.
        </p>
        <b-btn
          v-if="!displayMusicLibrary"
          size="sm"
          variant="light"
          @click="displayMusicLibrary = !displayMusicLibrary"
        >
          Show Music Library <v-icon name="music" class="mr-2" />
        </b-btn>
        <b-btn
          v-else
          size="sm"
          variant="light"
          @click="displayMusicLibrary = !displayMusicLibrary"
        >
          Hide Music Library <v-icon name="music" class="mr-2" />
        </b-btn>
        <span class="small ml-3">
          🎹 New! Pre-licensed music available on all accounts.
        </span>
      </b-col>
    </b-row>
    <!-- Music Library -->
    <b-row v-if="displayMusicLibrary">
      <b-col md="10" offset-md="1" class="mt-5">
        <MusicLibrarySelector @close="displayMusicLibrary = false" />
      </b-col>
    </b-row>

    <!-- Perhaps wrong format warning. -->
    <b-row v-if="showAudioWarning">
      <b-col md="8" offset="2" class="mt-5">
        <p class="alert alert-danger">
          Your selected file <strong>{{ audio_file.name }}</strong> does not
          have the correct extension. Double check that your file is in a
          supported format. ({{ $store.state.acceptedAudioFormats }})
        </p>
      </b-col>
    </b-row>
    <!-- Need more media warnings -->
    <b-row v-if="!safeToUpload">
      <b-col v-if="!isOverImageLimit" md="8" offset="2" class="mt-5">
        <p v-show="image_files.length != 0" class="alert alert-warning">
          You must also select an audio file.
        </p>
        <p v-show="audio_file && !isOverImageLimit" class="alert alert-warning">
          You must also select at least one image file.
        </p>
      </b-col>
    </b-row>
    <!-- Asset importer -->
    <b-row v-if="safeToUpload || uploadingHasBegun">
      <b-col md="8" offset="2" class="mt-3">
        <AssetImporter
          :show="show"
          :image_files="image_files"
          :audio_file="audio_file"
          :video_files="validVideos"
          @uploadStarted="uploadStarted"
        />
      </b-col>
    </b-row>
    <!-- Show list of uploaded media -->
    <b-row v-if="uploadedFiles.length > 0">
      <b-col md="8" offset="2" class="mt-5">
        <h5 class="mb-3">Uploaded files</h5>
        <p v-for="(file, idx) in uploadedFiles" :key="'uploaded_' + idx">
          <v-icon name="check" class="mr-2" /> {{ file }}
        </p>
      </b-col>
    </b-row>
    <input
      v-show="false"
      ref="audioSelect"
      type="file"
      :accept="$store.state.acceptedAudioFormats"
      class="input-file"
      @change="audioFileChange($event.target.name, $event.target.files)"
    />
    <input
      v-show="false"
      ref="imageSelect"
      type="file"
      multiple
      :accept="$store.state.acceptedImageFormats"
      class="input-file"
      @change="filesChange($event.target.name, $event.target.files)"
    />
    <input
      v-show="false"
      ref="videoSelect"
      type="file"
      :multiple="true"
      :accept="$store.state.acceptedVideoFormats"
      class="input-file"
      @change="videoFilesChange($event.target.name, $event.target.files)"
    />
  </div>
</template>

<script>
import AssetImporter from "@/components/show/AssetImporter";
import MusicLibrarySelector from "@/components/show/MusicLibrarySelector";
import map from "lodash/map";
import reverse from "lodash/reverse";
import filter from "lodash/filter";
import find from "lodash/find";
import each from "lodash/each";
import ImageLimitImportWarning from "@/components/show/ImageLimitImportWarning";
export default {
  name: "ImporterInitial",
  filters: {
    pluralize(word, amount) {
      if (amount > 1) {
        return `${word}s`;
      }
      return word;
    },
    toMMSS: function(s) {
      // Pad to 2 or 3 digits, default is 2
      function pad(n, z) {
        z = z || 2;
        return ("00" + n).slice(-z);
      }

      var ms = s % 1000;
      s = (s - ms) / 1000;
      var secs = s % 60;
      s = (s - secs) / 60;
      var mins = s % 60;

      return pad(mins) + ":" + pad(secs);
    }
  },
  components: {
    AssetImporter,
    MusicLibrarySelector,
    ImageLimitImportWarning
  },
  data() {
    return {
      image_files: [],
      audio_file: null,
      uploadingHasBegun: false,
      showAudioWarning: false,
      displayMusicLibrary: false,
      selectedImagesTotal: 0,
      video_files: [],
      videoDurationLimitSeconds:
        this.$store.state.user_doc.video_slides_duration_limit ||
        this.$store.state.videoImportLimitSeconds,
      validVideos: [],
      invalidVideos: []
    };
  },
  computed: {
    show() {
      return this.$store.state.show.currentShow;
    },
    show_audio() {
      return ((this.show || {}).data || {}).audio || null;
    },
    safeToUpload() {
      if (this.isOverImageLimit) {
        return false; // Check the image limit first.
      }
      if (this.isOverVideoLimit) {
        return false; // Check the video limit first.
      }
      if (this.isEmptyShow) {
        // Require both images and audio.
        return this.image_files.length > 0 && this.audio_file;
      } else {
        // Require just audio.
        return this.audio_file != null || this.show_audio;
      }
    },
    isOverImageLimit() {
      return this.image_files.length > this.$store.state.imageImportLimit;
    },
    isOverVideoLimit() {
      return this.validVideos.length > this.$store.state.videoImportLimit;
    },
    isEmptyShow() {
      return this.show_audio == null && this.show_images.length === 0;
    },
    show_images() {
      return ((this.show || {}).data || {}).images || [];
    },
    show_videos() {
      if (this.show.data.images) {
        return filter(this.show.data.images, i => {
          return i.type == "video";
        });
      }
      return [];
    },
    uploadedFiles() {
      var files = map(this.show_images, "name");
      if (this.show_audio) {
        files.push(this.show_audio.name);
      }
      return reverse(files);
    },
    dataAlreadyUploaded() {
      return this.show_audio != null && this.show_audio.error != true;
    }
  },
  methods: {
    uploadStarted() {
      // Reset the files for the next addition.
      this.uploadingHasBegun = true;
      this.image_files = [];
      this.video_files = [];
      this.validVideos = [];
      this.invalidVideos = [];
      this.audio_file = null;
    },
    selectImages() {
      this.$refs.imageSelect.click();
    },
    filesChange(fieldName, fileList) {
      if (!fileList.length) return;
      this.selectedImagesTotal = fileList.length;
      // append the files to FormData
      this.image_files = filter(fileList, (i, idx) => {
        console.log("idx", idx);
        if (idx < this.$store.state.imageImportLimit) {
          return i;
        }
      });
    },
    selectVideo() {
      this.$refs.videoSelect.click();
    },
    videoFilesChange(fieldName, fileList) {
      this.validVideos = [];
      this.invalidVideos = [];
      if (!fileList.length) return;
      // append the files to FormData
      this.video_files = map(fileList, i => {
        return i;
      });
      this.addVideoDurationInfo();
    },
    addVideoDurationInfo() {
      var _v = this;
      each(this.video_files, video_file => {
        var video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = function() {
          window.URL.revokeObjectURL(video.src);
          var duration = video.duration;
          video_file.duration = duration;
          console.log("duration", duration);
          console.log(video_file);
          var actualVideoDurationLimitSeconds =
            _v.videoDurationLimitSeconds + 2; // Give the user some leeway.
          if (duration < actualVideoDurationLimitSeconds) {
            console.log("valid video");
            _v.validVideos.push(video_file);
          } else {
            console.log("invalid video");
            _v.invalidVideos.push(video_file);
          }
        };
        video.src = URL.createObjectURL(video_file);
      });
    },
    selectAudio() {
      this.$refs.audioSelect.click();
    },
    audioFileChange(fieldName, fileList) {
      if (!fileList.length) return;
      // append the files to FormData
      this.audio_file = fileList[0];
      if (!this.audio_file) {
        return;
      }
      // Check that this is one of the available formats.
      // This check is really ONLY for iOS.
      // iOS does not honor any of the "audio/*" or extension 'accept' values
      // on input fields.

      var approvedFormats = this.$store.state.acceptedAudioFormats.split(",");
      var extension = find(approvedFormats, i => {
        if (this.audio_file.name.endsWith(i)) {
          return true;
        }
      });
      if (!extension) {
        this.showAudioWarning = true;
      }
    }
  }
};
</script>

<style lang="css" scoped></style>
